<template>
  <tr class="hover:bg-tiara-50/50">
    <td
      class="border-t border-tiara-300 align-middle whitespace-nowrap px-4 py-3 bg-white text-sm"
    >
      <div class="font-medium text-blueGray-800">
        {{
          submission.template ? submission.template.name : "Unknown Template"
        }}
      </div>
      <div class="text-sm text-gray-500 mt-1">
        <template v-if="otherParty">
          <div class="flex items-center space-x-2">
            <span>{{ otherParty.email }}</span>
            <a
              v-if="otherParty.profileHashedId"
              :href="`/admin/profiles/edit/${otherParty.profileHashedId}`"
              class="text-tiara-400 hover:text-tiara-600 transition-colors"
              target="_blank"
              title="View Profile"
            >
              <i class="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
            </a>
          </div>
        </template>
        <template v-else-if="firstParty">
          <div class="flex items-center space-x-2">
            <span>{{ firstParty.email }}</span>
            <a
              v-if="firstParty.profileHashedId"
              :href="`/admin/profiles/edit/${firstParty.profileHashedId}`"
              class="text-tiara-400 hover:text-tiara-600 transition-colors"
              target="_blank"
              title="View Profile"
            >
              <i class="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
            </a>
          </div>
        </template>
      </div>
    </td>
    <td
      class="border-t border-tiara-300 align-middle whitespace-nowrap px-4 py-3 bg-white text-sm"
    >
      <span
        class="px-2 py-1 rounded-full text-xs font-semibold"
        :class="{
          'bg-green-100 text-green-800': submission.status === 'completed',
          'bg-yellow-100 text-yellow-800': submission.status === 'pending',
          'bg-red-100 text-red-800':
            submission.status === 'declined' || submission.status === 'expired',
          'bg-gray-100 text-gray-800': ![
            'completed',
            'pending',
            'declined',
            'expired',
          ].includes(submission.status),
        }"
      >
        <span class="capitalize">{{ submission.status }}</span>
      </span>
    </td>
    <td
      class="border-t border-tiara-300 align-middle whitespace-nowrap px-4 py-3 bg-white text-sm text-gray-600"
    >
      {{ $dayjs(submission.createdAt).format("MMM D, YYYY h:mm A") }}
    </td>
    <td
      class="border-t border-tiara-300 align-middle whitespace-nowrap px-4 py-3 bg-white text-sm"
    >
      <template v-if="notedSourceSignatory">
        <div v-if="notedSourceSignatory.status === 'completed'">
          <span
            class="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 capitalize"
          >
            Completed
          </span>
          <div
            v-if="notedSourceSignatory.completedAt"
            class="text-[10px] text-gray-400 mt-1"
          >
            {{
              $dayjs(notedSourceSignatory.completedAt).format(
                "MMM D, YYYY h:mm A",
              )
            }}
          </div>
        </div>
        <div
          v-else-if="otherParty && otherParty.status === 'completed'"
          class="flex flex-col items-start space-y-1"
        >
          <span
            class="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
          >
            Ready to Countersign
          </span>
          <button
            class="bg-tiara-500 hover:bg-tiara-600 text-white text-xs px-2 py-1 rounded font-medium transition-colors"
            @click="openDocusealModal(notedSourceSignatory, 'countersign')"
          >
            <i class="fa-solid fa-pen-nib mr-1"></i> Countersign
          </button>
        </div>
        <div
          v-else-if="
            notedSourcePreparer && notedSourcePreparer.status !== 'completed'
          "
          class="flex flex-col items-start space-y-1"
        >
          <span
            class="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800"
          >
            Needs Pre-fill
          </span>
          <button
            class="bg-tiara-500 hover:bg-tiara-600 text-white text-xs px-2 py-1 rounded font-medium transition-colors"
            @click="openDocusealModal(notedSourcePreparer, 'prefill')"
          >
            <i class="fa-solid fa-pen-to-square mr-1"></i> Pre-fill Form
          </button>
        </div>
        <div v-else>
          <span
            class="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800"
          >
            Awaiting Expert Signature
          </span>
        </div>
      </template>
      <template v-else-if="notedSource">
        <span
          class="px-2 py-0.5 rounded-full text-xs font-semibold"
          :class="{
            'bg-green-100 text-green-800': notedSource.status === 'completed',
            'bg-yellow-100 text-yellow-800': notedSource.status === 'pending',
            'bg-gray-100 text-gray-800': !['completed', 'pending'].includes(
              notedSource.status,
            ),
          }"
        >
          <span class="capitalize">{{ notedSource.status }}</span>
        </span>
        <div
          v-if="notedSource.status == 'completed'"
          class="text-[10px] text-gray-400 mt-1"
        >
          {{ $dayjs(notedSource.completedAt).format("MMM D, YYYY h:mm A") }}
        </div>
      </template>
      <span v-else class="text-gray-400 italic">N/A</span>
    </td>
    <td
      class="border-t border-tiara-300 align-middle whitespace-nowrap px-4 py-3 bg-white text-sm"
    >
      <template v-if="otherParty">
        <div v-if="otherParty.status != 'awaiting'">
          <span
            class="px-2 py-0.5 rounded-full text-xs font-semibold"
            :class="{
              'bg-green-100 text-green-800': otherParty.status === 'completed',
              'bg-yellow-100 text-yellow-800': otherParty.status === 'pending',
              'bg-gray-100 text-gray-800': !['completed', 'pending'].includes(
                otherParty.status,
              ),
            }"
          >
            <span class="capitalize">{{ otherParty.status }}</span>
          </span>
          <div
            v-if="otherParty.status == 'completed'"
            class="text-[10px] text-gray-400 mt-1"
          >
            {{ $dayjs(otherParty.completedAt).format("MMM D, YYYY h:mm A") }}
          </div>
        </div>
        <div v-else class="flex flex-col">
          <div>
            <span
              class="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 capitalize"
              >{{ otherParty.status }}</span
            >
          </div>
          <div class="flex items-center space-x-2 mt-2">
            <a
              :href="urlPrefix + otherParty.slug"
              target="_blank"
              class="text-blue-500 hover:underline text-xs truncate max-w-[150px]"
              :title="urlPrefix + otherParty.slug"
            >
              {{ urlPrefix }}{{ otherParty.slug }}
            </a>
            <button
              class="text-[10px] bg-tiara-100 text-tiara-600 px-1.5 py-0.5 border border-tiara-200 rounded hover:bg-tiara-200"
              title="Copy URL"
              @click="copyUrl(urlPrefix + otherParty.slug)"
            >
              <i aria-hidden="true" class="fa-regular fa-copy"></i>
            </button>
            <button
              class="text-[10px] bg-tiara-100 text-tiara-600 px-1.5 py-0.5 border border-tiara-200 rounded hover:bg-tiara-200"
              title="Resend Email"
              @click="emailSignatureRequest"
            >
              <i aria-hidden="true" class="fa-regular fa-envelope"></i>
            </button>
          </div>
        </div>
      </template>
      <template v-else-if="firstParty">
        <div v-if="firstParty.status != 'awaiting'">
          <span
            class="px-2 py-0.5 rounded-full text-xs font-semibold"
            :class="{
              'bg-green-100 text-green-800': firstParty.status === 'completed',
              'bg-yellow-100 text-yellow-800': firstParty.status === 'pending',
              'bg-gray-100 text-gray-800': !['completed', 'pending'].includes(
                firstParty.status,
              ),
            }"
          >
            <span class="capitalize">{{ firstParty.status }}</span>
          </span>
          <div
            v-if="firstParty.status == 'completed'"
            class="text-[10px] text-gray-400 mt-1"
          >
            {{ $dayjs(firstParty.completedAt).format("MMM D, YYYY h:mm A") }}
          </div>
        </div>
        <div v-else class="flex flex-col">
          <div>
            <span
              class="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 capitalize"
              >{{ firstParty.status }}</span
            >
          </div>
          <div class="flex items-center space-x-2 mt-2">
            <a
              :href="urlPrefix + firstParty.slug"
              target="_blank"
              class="text-blue-500 hover:underline text-xs truncate max-w-[150px]"
              :title="urlPrefix + firstParty.slug"
            >
              {{ urlPrefix }}{{ firstParty.slug }}
            </a>
            <button
              class="text-[10px] bg-tiara-100 text-tiara-600 px-1.5 py-0.5 border border-tiara-200 rounded hover:bg-tiara-200"
              title="Copy URL"
              @click="copyUrl(urlPrefix + firstParty.slug)"
            >
              <i aria-hidden="true" class="fa-regular fa-copy"></i>
            </button>
            <button
              class="text-[10px] bg-tiara-100 text-tiara-600 px-1.5 py-0.5 border border-tiara-200 rounded hover:bg-tiara-200"
              title="Resend Email"
              @click="emailSignatureRequest"
            >
              <i aria-hidden="true" class="fa-regular fa-envelope"></i>
            </button>
          </div>
        </div>
      </template>
      <span v-else class="text-gray-400 italic">N/A</span>
    </td>
    <td
      class="border-t border-tiara-300 align-middle whitespace-nowrap px-4 py-3 bg-white text-center"
    >
      <div class="flex justify-center space-x-2">
        <button
          class="text-xs bg-blueDianne px-2 py-1 text-white rounded hover:bg-blueDianne/80"
          title="Download"
          @click="downloadSubmission"
        >
          <i aria-hidden="true" class="fa-solid fa-download m-[2.5px]"></i>
        </button>
        <button
          v-if="!submission.archivedAt"
          class="text-xs bg-red-400 px-2 py-1 text-white rounded hover:bg-red-600"
          title="Archive"
          @click="toggleArchiveModal"
        >
          <i aria-hidden="true" class="fa-solid fa-box-archive m-[2.5px]"></i>
        </button>
        <button
          v-else
          class="text-xs bg-green-400 px-2 py-1 text-white rounded hover:bg-green-600"
          title="Unarchive"
          @click="unarchiveSubmission"
        >
          <i aria-hidden="true" class="fa-solid fa-box-open m-[2.5px]"></i>
        </button>
      </div>

      <regular-modal ref="archiveModal">
        <template #header>
          <h4 class="text-xl font-headline">Archive Contract</h4>
        </template>
        <template #body>
          <div class="p-6">
            <p>
              Are you sure you want to archive the contract
              <strong>{{
                submission.template ? submission.template.name : "this contract"
              }}</strong>
              for
              <span v-if="otherParty">{{ otherParty.email }}</span
              ><span v-else-if="firstParty">{{ firstParty.email }}</span
              >?
            </p>
          </div>
        </template>
        <template #footer>
          <button
            class="text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
            type="button"
            @click="toggleArchiveModal"
          >
            Cancel
          </button>
          <button
            class="bg-red-500 text-white active:bg-red-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
            type="button"
            @click="archiveSubmission"
          >
            Archive
          </button>
        </template>
      </regular-modal>

      <right-side-modal ref="docusealModal" :showing="false">
        <template #header>
          <h4 class="text-xl font-headline">{{ activeModalTitle }}</h4>
        </template>
        <template #body>
          <div class="px-5 py-5">
            <docuseal-form
              v-if="activeEmbedSrc"
              :src="activeEmbedSrc"
              :options="{ hideHeader: true }"
              @completed="onDocusealCompleted"
            />
          </div>
        </template>
      </right-side-modal>
    </td>
  </tr>
</template>

<script>
import { DocusealForm } from "@docuseal/vue";
import RegularModal from "../Modals/Regular.vue";
import RightSideModal from "../Modals/RightSide.vue";

export default {
  name: "Contract Submission",
  components: {
    RegularModal,
    RightSideModal,
    DocusealForm,
  },
  props: {
    submission: Object,
  },
  data() {
    return {
      urlPrefix: "https://docuseal.notedsource.io/s/",
      activeEmbedSrc: null,
      activeModalTitle: "",
    };
  },
  computed: {
    notedSource() {
      return this.submission.submitters?.find((submitter) => {
        return submitter.role === "NotedSource";
      });
    },
    notedSourcePreparer() {
      return this.submission.submitters?.find((submitter) => {
        return submitter.role === "NotedSource (Preparation)";
      });
    },
    notedSourceSignatory() {
      return this.submission.submitters?.find((submitter) => {
        return submitter.role === "NotedSource (Signature)";
      });
    },
    otherParty() {
      return this.submission.submitters?.find((submitter) => {
        return (
          submitter.role !== "NotedSource" &&
          submitter.role !== "NotedSource (Preparation)" &&
          submitter.role !== "NotedSource (Signature)" &&
          submitter.role !== "First Party"
        );
      });
    },
    firstParty() {
      return this.submission.submitters?.find((submitter) => {
        return submitter.role === "First Party";
      });
    },
  },
  methods: {
    openDocusealModal(submitter, actionType) {
      this.activeEmbedSrc =
        submitter.embedSrc || this.urlPrefix + submitter.slug;
      this.activeModalTitle =
        actionType === "countersign"
          ? "Countersign Contract"
          : "Pre-fill Contract";
      this.$refs.docusealModal.toggleModal();
    },
    onDocusealCompleted() {
      this.$refs.docusealModal.toggleModal();
      this.activeEmbedSrc = null;
      this.$toast.success("Contract updated successfully");
      this.$emit("refresh");
    },
    copyUrl(url) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          this.$toast.success("URL copied to clipboard!");
        })
        .catch(() => {
          this.$toast.error("Failed to copy URL");
        });
    },
    downloadSubmission() {
      this.$api
        .axios({
          url: `/contract/${this.submission.id}/download/`,
          method: "GET",
          responseType: "blob",
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `contract_${this.submission.id}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    },
    toggleArchiveModal() {
      this.$refs.archiveModal.toggleModal();
    },
    archiveSubmission() {
      this.$api.axios
        .post(`/contract/${this.submission.id}/archive/`)
        .then(() => {
          this.$toast.success("Contract archived");
          this.toggleArchiveModal();
          this.$emit("refresh");
        });
    },
    unarchiveSubmission() {
      this.$api.axios
        .post(`/contract/${this.submission.id}/unarchive/`)
        .then(() => {
          this.$toast.success("Contract unarchived");
          this.$emit("refresh");
        });
    },
    emailSignatureRequest() {
      this.$api.axios
        .get(`/contract/${this.submission.id}/send-email/`)
        .then(() => {
          this.$toast.success(
            `Signature request email sent to ${this.otherParty.email}`,
          );
        });
    },
  },
};
</script>
